/**
 * Set a single bit in a mask, returning the updated mask.
 * Handles promotion from integer to dense array when bitIndex >= 32.
 * @param {number|number[]} mask - Current system mask
 * @param {number} sysBitIndex - The bit position to set
 * @returns {number|number[]} Updated mask
 */
export function setBitAtIndex(mask, sysBitIndex) {
	const maskWord = sysBitIndex >>> 5;
	const offsetSysMask = 1 << (sysBitIndex & 0x1f);

	if (!Array.isArray(mask)) {
		// Common case: Fewer than 32 systems, so we can use a single mask
		if (maskWord === 0) {
			return mask | offsetSysMask;
		}
		// First time seeing index larger than 32, so we need to use an array of mask to handle overflow bits
		mask = [mask];
	}

	while (mask.length <= maskWord) mask.push(0);
	mask[maskWord] |= offsetSysMask;
	return mask;
}

/**
 * Convert a list of system IDs to a bitmask
 * @param {Iterable<string>} sysIds - System IDs to encode
 * @param {Object} sysId2BitIndex - Map of systemId to bitIndex
 * @returns {number|number[]} Bitmask representing the list of system IDs
 */
export function sysIdsToMask(sysIds, sysId2BitIndex) {
	let mask = 0;

	for (const id of sysIds) {
		const bitIndex = sysId2BitIndex[id];
		if (bitIndex === undefined) continue;
		mask = setBitAtIndex(mask, bitIndex);
	}

	return mask;
}

/**
 * Convert a bitmask to a list of system IDs
 * @param {number|number[]} elmtMask - Element's system bitmask
 * @param {Object} sysId2BitIndex - Map of systemId -> bitIndex
 * @returns {string[]} List of system IDs whose bits are set in the mask
 */
export function maskToSysIds(elmtMask, sysId2BitIndex) {
	const sysIds = [];

	for (const id in sysId2BitIndex) {
		if (id === 'count') continue;
		if (isElementInSystem(sysId2BitIndex[id], elmtMask)) {
			sysIds.push(id);
		}
	}

	return sysIds;
}

/**
 * Check if a single system bit is set in an element mask.
 * @param {number|undefined} sysBitIndex - Value of sysId2BitIndex[sysId]
 * @param {number|number[]} elmtMask - Value of dbId2SysBitMask[dbId]
 * @returns {boolean} True if element belongs to the system, false otherwise
 */
export function isElementInSystem(sysBitIndex, elmtMask) {
	if (sysBitIndex === undefined || !elmtMask) return false;

	const sysBitWord = sysBitIndex >>> 5;
	const sysBitMask = 1 << (sysBitIndex & 0x1f);

	if (Array.isArray(elmtMask)) return Boolean(elmtMask[sysBitWord] & sysBitMask);
	if (sysBitWord > 0) return false;
	return Boolean(elmtMask & sysBitMask);
}

/**
 * Compare two bit masks for equality
 * @param {number|number[]} a
 * @param {number|number[]} b
 * @returns {boolean}
 */
export function masksEqual(a, b) {
	if (a === b) return true;

	const aIsArr = Array.isArray(a);
	const bIsArr = Array.isArray(b);

	// Both are numbers, check if they are equal
	if (!aIsArr && !bIsArr) return a === b;

	// One is an array, the other is a number, they cannot be equal
	if (aIsArr !== bIsArr) {
		return false;
	}

	// Both are arrays, check if all bits are equal in both masks
	const len = Math.max(a.length, b.length);
	for (let i = 0; i < len; i++) {
		if ((a[i] || 0) !== (b[i] || 0)) return false;
	}

	return true;
}

/**
 * Check if two masks have any overlapping bits (share at least one system)
 * @param {number|number[]} a
 * @param {number|number[]} b
 * @returns {boolean}
 */
export function masksOverlap(a, b) {
	if (!a || !b) return false;

	const aIsArr = Array.isArray(a);
	const bIsArr = Array.isArray(b);

	// Simple case, both are masks < 32 systems
	if (!aIsArr && !bIsArr) return (a & b) !== 0;

	// One is an array, the other is a number, check if they share at least one bit
	if (!aIsArr || !bIsArr) {
		const arr = aIsArr ? a : b;
		const num = aIsArr ? b : a;
		return (arr[0] & num) !== 0;
	}

	// Both are arrays, check if any bit is set in both masks
	for (let i = 0; i < a.length && i < b.length; i++) {
		if (a[i] & b[i]) return true;
	}

	return false;
}

export const SystemBitMask = {
	setBitAtIndex,
	sysIdsToMask,
	maskToSysIds,
	isElementInSystem,
	masksEqual,
	masksOverlap,
};
